import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

const tipImobilLabels = {
  apartament: "apartamentul",
  casa: "casa",
  spatiu_comercial: "spatiul comercial",
};

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());

  const tipImobilText = data.imobilTip === "altul"
    ? (data.imobilTipAltul || "imobilul")
    : (tipImobilLabels[data.imobilTip] || "imobilul");
  const ascundeCamere = data.imobilTip === "spatiu_comercial" || data.imobilTip === "altul";
  const dataInceputFmt = data.dataInceput
    ? fmtDate(new Date(data.dataInceput))
    : "___";
  const durateLuni = parseInt(data.durata) || 12;
  let dataEndFmt = "___";
  if (data.dataInceput) {
    const end = new Date(data.dataInceput);
    end.setMonth(end.getMonth() + durateLuni);
    dataEndFmt = fmtDate(end);
  }

  const ciPropFmt = !data.proprietarPJ && data.proprietarCI ? formatCI(data.proprietarCI) : "___ ___";
  const ciChirFmt = !data.chiriasPJ && data.chiriasCI ? formatCI(data.chiriasCI) : "___ ___";

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = { ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" };
  const bullet = { ...p, paddingLeft: isMobile ? "20px" : "36px", margin: "2px 0" };

  const interdictiiPreview = [];
  if (!data.fumatPermis) interdictiiPreview.push("fumatul in interior");
  if (!data.animalePermise) interdictiiPreview.push("detinerea de animale (fara acord scris)");
  interdictiiPreview.push("schimbarea incuietorilor fara acord");

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Inchiriere
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: 0,
        }}>
          Nr. ___ din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>

        <p style={p}><span style={bold}>1.1. Proprietarul (Locatorul):</span></p>
        {data.proprietarPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.proprietarFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.proprietarCUI || "________________________"}</span></p>
            {data.proprietarRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.proprietarRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.proprietarSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.proprietarReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.proprietarNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.proprietarCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciPropFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.proprietarAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Chiriasul (Locatarul):</span></p>
        {data.chiriasPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.chiriasFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.chiriasCUI || "________________________"}</span></p>
            {data.chiriasRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.chiriasRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.chiriasSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.chiriasReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.chiriasNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.chiriasCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciChirFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.chiriasAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de inchiriere in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Proprietarul inchiriaza Chiriasului imobilul de tip <span style={bold}>{tipImobilText}</span> situat in {data.imobilAdresa || "____________________________"}{!ascundeCamere && data.imobilCamere ? `, compus din ${data.imobilCamere} camere` : ""}, cu suprafata utila de {data.imobilSuprafata || "___"} mp{data.imobilEtaj ? `, etajul ${data.imobilEtaj}` : ""}.
        </p>
        <p style={p}>
          <span style={bold}>2.2.</span> Imobilul se preda {data.mobilat ? "mobilat si utilat" : "nemobilat"} conform Anexei 1 – Proces-verbal de predare-primire, care include inventarul bunurilor, starea acestora si indexurile contoarelor.
        </p>
      </div>

      {/* III. DESTINATIA IMOBILULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DESTINATIA IMOBILULUI</h3>
        {data.imobilTip === "spatiu_comercial" ? (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Imobilul va fi folosit exclusiv pentru desfasurarea de activitati comerciale sau profesionale. Este interzisa folosirea spatiului ca locuinta.
            </p>
            {data.nrMaxPersoane && (
              <p style={p}>
                <span style={bold}>3.2.</span> Numarul maxim de persoane care pot desfasura activitate in imobil este de <span style={bold}>{data.nrMaxPersoane}</span> persoane.
              </p>
            )}
          </>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Imobilul va fi folosit exclusiv ca locuinta. Este interzisa desfasurarea de activitati comerciale sau inregistrarea de sedii sociale fara acordul scris al Proprietarului.
            </p>
            <p style={p}>
              <span style={bold}>3.2.</span> Numarul maxim de persoane care pot locui in imobil este de <span style={bold}>{data.nrMaxPersoane || "___"}</span> persoane.
            </p>
          </>
        )}
      </div>

      {/* IV. DURATA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. DURATA CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> Contractul se incheie pe durata de <span style={bold}>{durateLuni} luni</span>, incepand cu data de <span style={bold}>{dataInceputFmt}</span> si pana la data de <span style={bold}>{dataEndFmt}</span>.
        </p>
        <p style={p}>
          <span style={bold}>4.2.</span> Prelungirea se poate face doar prin acord scris (act aditional), cu minimum {data.notificareZile || "30"} zile inainte de expirare.
        </p>
      </div>

      {/* V. PRETUL SI MODALITATEA DE PLATA */}
      <h3 style={sectionTitle()}>V. PRETUL SI MODALITATEA DE PLATA</h3>
      <p style={p}>
        <span style={bold}>5.1.</span> Chiria lunara este de <span style={bold}>{data.chirie || "___"} {data.valuta || "EUR"}</span>, platibila pana la data de {data.ziuaPlata || "___"} a fiecarei luni.
      </p>
      <p style={p}>
        <span style={bold}>5.2.</span> Plata se face prin transfer bancar sau numerar, cu confirmare scrisa, cu respectarea legislatiei privind plafonul platilor in numerar.
      </p>
      <p style={p}>
        <span style={bold}>5.3.</span> Garantia este de <span style={bold}>{data.chirie && data.garantie ? Math.round(Number(data.chirie) * Number(data.garantie)) : "___"} {data.valuta || "EUR"}</span> (echivalentul a {data.garantie === "1" ? "1 chirie lunara" : `${data.garantie || "___"} chirii lunare`}), achitata la semnarea contractului.
      </p>
      <p style={p}>
        <span style={bold}>5.4.</span> Garantia se restituie la incetarea contractului, dupa predarea imobilului in stare corespunzatoare, minus eventualele:
      </p>
      <p style={bullet}>- chirii restante</p>
      <p style={bullet}>- utilitati restante</p>
      <p style={bullet}>- daune produse</p>
      {data.penalizareActiva && (
        <>
          <p style={{ ...p, marginTop: "8px" }}>
            <span style={bold}>5.5.</span> Intarzierea la plata atrage penalitati de {data.penalizare || "0.1"}% pe zi din suma datorata, incepand cu ziua urmatoare scadentei.
          </p>
          <p style={p}>
            <span style={bold}>5.6.</span> Penalitatile totale nu pot depasi valoarea chiriei restante.
          </p>
        </>
      )}

      {/* VI. UTILITATI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VI. UTILITATI</h3>
        {data.utilitatiIncluse ? (
          <p style={p}>
            <span style={bold}>6.1.</span> Chiria lunara include costul utilitatilor (apa, gaz, energie electrica, internet), in limita unui consum rezonabil.
          </p>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>6.1.</span> Chiriasul suporta costul utilitatilor (energie electrica, apa, gaz, internet etc.) conform consumului real, pe baza facturilor si indexurilor contoarelor.
            </p>
            <p style={p}>
              <span style={bold}>6.2.</span> Facturile vor fi achitate in termen de maximum {data.zilePlatUtilitati || "15"} zile de la primire.
            </p>
          </>
        )}
      </div>

      {/* VII. OBLIGATIILE PROPRIETARULUI */}
      <h3 style={sectionTitle()}>VII. OBLIGATIILE PROPRIETARULUI</h3>
      {[
        "Sa predea imobilul in stare corespunzatoare de folosinta.",
        "Sa asigure folosinta linistita a imobilului.",
        "Sa efectueze reparatiile majore si structurale.",
        "Sa nu tulbure folosinta Chiriasului, cu exceptia verificarilor efectuate cu preaviz de minimum 24 ore.",
        "Sa declare contractul la autoritatile competente, in conditiile legii.",
      ].map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>7.{i + 1}.</span> {text}
        </p>
      ))}

      {/* VIII. OBLIGATIILE CHIRIASULUI */}
      <h3 style={sectionTitle()}>VIII. OBLIGATIILE CHIRIASULUI</h3>
      {[
        "Sa plateasca chiria si utilitatile la termenele stabilite.",
        "Sa foloseasca imobilul cu prudenta si conform destinatiei.",
        "Sa nu aduca modificari fara acordul scris al Proprietarului.",
        "Sa permita accesul Proprietarului pentru verificari, cu preaviz de 24 ore.",
        "Sa anunte imediat orice defectiune aparuta.",
        "Sa efectueze reparatiile minore si de intretinere curenta.",
      ].map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>8.{i + 1}.</span> {text}
        </p>
      ))}
      <p style={indent}>
        <span style={bold}>8.7.</span> {!data.subinchirierePermisa
          ? "Este interzisa subinchirierea fara acordul scris al Proprietarului."
          : "Subinchirierea este permisa numai cu acordul scris al Proprietarului."}
      </p>
      <p style={indent}><span style={bold}>8.8.</span> Este interzis:</p>
      {interdictiiPreview.map((text, i) => (
        <p key={i} style={bullet}>- {text}</p>
      ))}
      {(() => {
        let nr = 9;
        return (
          <>
            <p style={indent}>
              <span style={bold}>8.{nr++}.</span> {data.fumatPermis
                ? "Fumatul este permis, cu obligatia chiriasului de a aerisi si de a preveni deteriorarea finisajelor."
                : "Fumatul este strict interzis in interiorul imobilului."}
            </p>
            {data.animalePermise && (
              <p style={indent}>
                <span style={bold}>8.{nr++}.</span> Animalele de companie sunt permise, cu conditia ca acestea sa nu produca deteriorari imobilului sau disconfort vecinilor.
              </p>
            )}
            <p style={indent}>
              <span style={bold}>8.{nr}.</span> La incetarea contractului, Chiriasul va preda imobilul in starea initiala, mai putin uzura normala.
            </p>
          </>
        );
      })()}

      {/* IX. INCETAREA CONTRACTULUI */}
      <h3 style={sectionTitle()}>IX. INCETAREA CONTRACTULUI</h3>
      <p style={p}><span style={bold}>9.1.</span> Contractul inceteaza:</p>
      {[
        "la expirarea duratei",
        "prin acordul partilor",
        "prin reziliere pentru neexecutarea obligatiilor",
        `prin denuntare unilaterala cu preaviz de ${data.notificareZile || "30"} zile`,
      ].map((text, i) => (
        <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
          <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcd"[i]})</span> {text}
        </p>
      ))}
      <p style={{ ...p, marginTop: "12px" }}>
        <span style={bold}>9.2.</span> Daca Chiriasul paraseste imobilul inainte de termen{data.pierdereGarantie ? ", garantia poate fi retinuta pentru acoperirea prejudiciilor, a chiriei restante sau a nerespectarii termenului de preaviz." : ", plateste echivalentul unei luni de chirie drept despagubire."}
      </p>
      <p style={{ ...p, marginTop: "12px" }}>
        <span style={bold}>9.3.</span> Neplata chiriei pentru o perioada de {data.zileNeplatReziliere || "30"} zile consecutive atrage rezilierea de plin drept, cu notificare prealabila.
      </p>

      {/* X. RASPUNDERE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>X. RASPUNDERE</h3>
        <p style={indent}><span style={bold}>10.1.</span> Chiriasul raspunde pentru daunele produse din culpa sa.</p>
        <p style={indent}><span style={bold}>10.2.</span> Uzura normala nu constituie dauna.</p>
      </div>

      {/* XI. FORTA MAJORA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>XI. FORTA MAJORA</h3>
        <p style={indent}><span style={bold}>11.1.</span> Forta majora exonereaza de raspundere partea care o invoca, in conditiile legii.</p>
        <p style={indent}><span style={bold}>11.2.</span> Partea care invoca forta majora are obligatia de a notifica cealalta parte in termen de 5 zile de la aparitia evenimentului.</p>
      </div>

      {/* XII. LITIGII */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>XII. LITIGII</h3>
        <p style={indent}><span style={bold}>12.1.</span> Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de instantele competente.</p>
      </div>

      {/* XIII. DISPOZITII FINALE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>XIII. DISPOZITII FINALE</h3>
        <p style={indent}><span style={bold}>13.1.</span> Prezentul contract poate constitui titlu executoriu numai in conditiile prevazute de lege.</p>
        <p style={indent}><span style={bold}>13.2.</span> Orice modificare se face prin act aditional.</p>
        <p style={indent}><span style={bold}>13.3.</span> Contractul se incheie in 2 exemplare originale.</p>
      </div>

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            PROPRIETAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.proprietarPJ
              ? (data.proprietarFirma ? `${data.proprietarFirma} prin ${data.proprietarReprezentant || ""}` : "")
              : (data.proprietarNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            CHIRIAS
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.chiriasPJ
              ? (data.chiriasFirma ? `${data.chiriasFirma} prin ${data.chiriasReprezentant || ""}` : "")
              : (data.chiriasNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ContractPreview;
